import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { ContentCopyRounded, DocumentScannerRounded } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useParams } from 'react-router-dom';
import server from '../../../services/server';
import useErrors from '../../../hooks/useErrors';
import DocumentInputMasked from '../../../components/DocumentInputMasked';
import PhoneInputMasked from '../../../components/PhoneInputMasked';
import ZipCodeInputMasked from '../../../components/ZipCodeInputMasked';
import CreditCardInputMasked from '../../../components/CreditCardInputMasked';
import { PriceInputMasked } from '../../../components/PriceMask';
import { payment_status } from '../../../services/helpers';

const FAILED_RETRYABLE_STATUSES = new Set([
    'REPROVED_BY_RISK_ANALYSIS',
    'CREDIT_CARD_CAPTURE_REFUSED',
    'ATTEMP',
]);

const AWAITING_PAYMENT_STATUSES = new Set(['PENDING', 'WAITING']);

function isAwaitingBoletoOrPix(paymentStatus, paymentData) {
    if (!paymentData || !AWAITING_PAYMENT_STATUSES.has(paymentStatus)) return false;
    const type = paymentData.billingType;
    return (
        type === 'BOLETO' ||
        type === 'PIX' ||
        Boolean(paymentData.bankSlipUrl) ||
        Boolean(paymentData.invoiceUrl) ||
        Boolean(paymentData.chargePix)
    );
}

const paymentSchema = Yup.object().shape({
    billingType: Yup.string().required(),
    name: Yup.string().when('billingType', {
        is: 'CREDIT_CARD',
        then: s => s.required('Nome é obrigatório'),
    }),
    email: Yup.string().when('billingType', {
        is: 'CREDIT_CARD',
        then: s => s.email('E-mail inválido').required('E-mail é obrigatório'),
    }),
    cpfCnpj: Yup.string().when('billingType', {
        is: 'CREDIT_CARD',
        then: s => s.required('CPF/CNPJ é obrigatório'),
    }),
    phone: Yup.string().when('billingType', {
        is: 'CREDIT_CARD',
        then: s => s.required('Telefone é obrigatório'),
    }),
    mobilePhone: Yup.string().when('billingType', {
        is: 'CREDIT_CARD',
        then: s => s.required('Celular é obrigatório'),
    }),
    postalCode: Yup.string().when('billingType', {
        is: 'CREDIT_CARD',
        then: s => s.required('CEP é obrigatório'),
    }),
    address: Yup.string().when('billingType', {
        is: 'CREDIT_CARD',
        then: s => s.required('Endereço é obrigatório'),
    }),
    addressNumber: Yup.string().when('billingType', {
        is: 'CREDIT_CARD',
        then: s => s.required('Número é obrigatório'),
    }),
    province: Yup.string().when('billingType', {
        is: 'CREDIT_CARD',
        then: s => s.required('Bairro é obrigatório'),
    }),
    creditCard: Yup.string().when('billingType', {
        is: 'CREDIT_CARD',
        then: s => s.required('Número do cartão é obrigatório'),
    }),
    creditCardHolderName: Yup.string().when('billingType', {
        is: 'CREDIT_CARD',
        then: s => s.required('Nome no cartão é obrigatório'),
    }),
    creditCardExpiryMonth: Yup.string().when('billingType', {
        is: 'CREDIT_CARD',
        then: s =>
            s
                .required('Mês é obrigatório')
                .matches(/^(0[1-9]|1[0-2])$/, 'Mês inválido (MM)'),
    }),
    creditCardExpiryYear: Yup.string().when('billingType', {
        is: 'CREDIT_CARD',
        then: s =>
            s
                .required('Ano é obrigatório')
                .matches(/^(20)\d{2}$/, 'Ano inválido (AAAA)')
                .test('valid-year', 'Ano no passado', v => Number(v) >= new Date().getFullYear()),
    }),
    creditCardCcv: Yup.string().when('billingType', {
        is: 'CREDIT_CARD',
        then: s => s.required('CVV é obrigatório').matches(/^\d{3,4}$/, 'CVV inválido'),
    }),
});

export function CustomizationPaymentStep({ contract, mutate, customAmount }) {
    const { propostaId } = useParams();
    const { setErrors, enqueueSnackbar } = useErrors();

    const order = contract?.customizationOrder;
    const paymentStatus = order?.paymentStatus ?? 'WAITING';
    const paymentData = order?.paymentData ?? null;

    const isPaid = ['CONFIRMED', 'RECEIVED', 'CONCLUDED'].includes(paymentStatus);
    const awaitingSlip = isAwaitingBoletoOrPix(paymentStatus, paymentData);
    const showPaymentForm =
        !isPaid &&
        !awaitingSlip &&
        (paymentStatus === 'WAITING' ||
            !paymentData ||
            FAILED_RETRYABLE_STATUSES.has(paymentStatus));
    const showFailedRetryHint = FAILED_RETRYABLE_STATUSES.has(paymentStatus);

    const {
        values,
        getFieldProps,
        setFieldValue,
        errors,
        handleSubmit,
        isSubmitting,
        isValid,
    } = useFormik({
        initialValues: {
            billingType: 'CREDIT_CARD',
            name: '',
            email: '',
            cpfCnpj: '',
            phone: '',
            mobilePhone: '',
            postalCode: '',
            address: '',
            addressNumber: '',
            addressComplement: '',
            province: '',
            creditCard: '',
            creditCardHolderName: '',
            creditCardExpiryMonth: '',
            creditCardExpiryYear: '',
            creditCardCcv: '',
            installmentCount: '1',
        },
        validationSchema: paymentSchema,
        validateOnChange: true,
        onSubmit: async (vals, { setSubmitting }) => {
            try {
                setSubmitting(true);
                await server.put(`subscription/${propostaId}/payment`, {
                    ...vals,
                    type: 'customization',
                    customizationOrderId: order?.id,
                });
                await mutate();
                enqueueSnackbar('Pagamento da customização processado.');
            } catch (error) {
                setErrors(error);
            } finally {
                setSubmitting(false);
            }
        },
    });

    const handleFreeCustomization = async () => {
        try {
            await server.put(`subscription/${propostaId}/payment`, {
                type: 'customization',
                customizationOrderId: order?.id,
            });
            await mutate();
            enqueueSnackbar('Customização confirmada.');
        } catch (error) {
            setErrors(error);
        }
    };

    const handleCopyPix = () => {
        const payload = paymentData?.chargePix?.payload;
        if (payload) {
            navigator.clipboard.writeText(payload).then(() => {
                enqueueSnackbar('Código PIX copiado.');
            });
        }
    };

    if (isPaid) {
        return (
            <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={2}>
                    <Box>
                        <Typography variant="h3" component="h2">
                            Customizações e adicionais
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Cobrança única dos itens de customização e adicionais contratados na proposta
                            (setup, personalizações e serviços avulsos).
                        </Typography>
                    </Box>
                    <Divider />
                    <Alert severity="success">
                        Pagamento das customizações confirmado
                        {payment_status[paymentStatus] ? ` — ${payment_status[paymentStatus]}` : ''}.
                    </Alert>
                    {customAmount > 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            Valor das customizações:{' '}
                            {Number(customAmount).toLocaleString('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                            })}
                        </Typography>
                    ) : null}
                    {order?.Order_products?.length > 0 ? (
                        <Stack spacing={1}>
                            {order.Order_products.map(item => (
                                <Typography key={item.id} variant="body2" color="text.secondary">
                                    • {item.Product?.name || item.productName || 'Item'} —{' '}
                                    {Number(item.total ?? item.price ?? 0).toLocaleString('pt-BR', {
                                        style: 'currency',
                                        currency: 'BRL',
                                    })}
                                </Typography>
                            ))}
                        </Stack>
                    ) : null}
                </Stack>
            </Paper>
        );
    }

    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
                <Box>
                    <Typography variant="h3" component="h2">
                        Customizações e adicionais
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Cobrança única dos itens de customização e adicionais contratados na proposta
                        (setup, personalizações e serviços avulsos).
                    </Typography>
                </Box>
                <Divider />

                <Typography variant="body1">
                    Conclua o pagamento das customizações para prosseguir.
                </Typography>

                {/* Lista de itens */}
                {order?.Order_products?.map(item => (
                    <Stack direction="row" spacing={2} key={item.id}>
                        <TextField
                            label="Item"
                            fullWidth
                            value={item.Product?.name ?? item.productId}
                            InputProps={{ readOnly: true }}
                        />
                        <TextField
                            label="Valor"
                            fullWidth
                            value={Number(item.price ?? item.Product?.price ?? 0)}
                            InputProps={{ readOnly: true, inputComponent: PriceInputMasked }}
                        />
                    </Stack>
                ))}

                <TextField
                    label="Total"
                    fullWidth
                    value={customAmount === 0 ? 'Gratuito' : customAmount}
                    InputProps={{
                        readOnly: true,
                        ...(customAmount > 0 ? { inputComponent: PriceInputMasked } : {}),
                    }}
                />

                {/* Customização gratuita */}
                {customAmount === 0 && (
                    <Button variant="contained" onClick={handleFreeCustomization}>
                        Confirmar customizações (sem custo)
                    </Button>
                )}

                {/* Customização paga */}
                {customAmount > 0 && showPaymentForm && (
                    <>
                        {showFailedRetryHint && (
                            <Alert severity="warning">
                                {payment_status[paymentStatus] ?? `Status: ${paymentStatus}`}. Você pode tentar
                                novamente.
                            </Alert>
                        )}

                        <ToggleButtonGroup
                            exclusive
                            value={values.billingType}
                            onChange={(_, v) => v && setFieldValue('billingType', v)}
                            fullWidth
                        >
                            <ToggleButton value="CREDIT_CARD">Cartão de Crédito</ToggleButton>
                            <ToggleButton value="BOLETO">Boleto</ToggleButton>
                            <ToggleButton value="PIX">PIX</ToggleButton>
                        </ToggleButtonGroup>

                        {values.billingType === 'CREDIT_CARD' && (
                            <Stack spacing={2}>
                                <Divider>Dados do titular do cartão</Divider>

                                <Stack direction="row" columnGap={2}>
                                    <TextField
                                        label="CPF/CNPJ"
                                        {...getFieldProps('cpfCnpj')}
                                        fullWidth
                                        error={!!errors.cpfCnpj}
                                        helperText={errors.cpfCnpj}
                                        InputProps={{ inputComponent: DocumentInputMasked }}
                                    />
                                    <TextField
                                        label="E-mail"
                                        {...getFieldProps('email')}
                                        fullWidth
                                        error={!!errors.email}
                                        helperText={errors.email}
                                    />
                                </Stack>
                                <TextField
                                    label="Nome completo"
                                    {...getFieldProps('name')}
                                    fullWidth
                                    error={!!errors.name}
                                    helperText={errors.name}
                                />
                                <Stack direction="row" columnGap={1}>
                                    <TextField
                                        label="Telefone"
                                        {...getFieldProps('phone')}
                                        fullWidth
                                        error={!!errors.phone}
                                        helperText={errors.phone}
                                        InputProps={{ inputComponent: PhoneInputMasked }}
                                    />
                                    <TextField
                                        label="Celular"
                                        {...getFieldProps('mobilePhone')}
                                        fullWidth
                                        error={!!errors.mobilePhone}
                                        helperText={errors.mobilePhone}
                                        InputProps={{ inputComponent: PhoneInputMasked }}
                                    />
                                </Stack>
                                <Stack direction="row" columnGap={2}>
                                    <TextField
                                        label="CEP"
                                        {...getFieldProps('postalCode')}
                                        fullWidth
                                        error={!!errors.postalCode}
                                        helperText={errors.postalCode}
                                        InputProps={{ inputComponent: ZipCodeInputMasked }}
                                    />
                                    <TextField
                                        label="Bairro"
                                        {...getFieldProps('province')}
                                        fullWidth
                                        error={!!errors.province}
                                        helperText={errors.province}
                                    />
                                </Stack>
                                <Stack direction="row" columnGap={2}>
                                    <TextField
                                        label="Endereço"
                                        {...getFieldProps('address')}
                                        fullWidth
                                        error={!!errors.address}
                                        helperText={errors.address}
                                    />
                                    <TextField
                                        sx={{ width: 200 }}
                                        label="Número"
                                        {...getFieldProps('addressNumber')}
                                        error={!!errors.addressNumber}
                                        helperText={errors.addressNumber}
                                    />
                                </Stack>
                                <TextField
                                    label="Complemento"
                                    {...getFieldProps('addressComplement')}
                                    fullWidth
                                />

                                <Divider>Dados do cartão</Divider>

                                <TextField
                                    label="Número do Cartão"
                                    {...getFieldProps('creditCard')}
                                    fullWidth
                                    error={!!errors.creditCard}
                                    helperText={errors.creditCard}
                                    InputProps={{ inputComponent: CreditCardInputMasked }}
                                />
                                <TextField
                                    label="Nome no Cartão"
                                    {...getFieldProps('creditCardHolderName')}
                                    fullWidth
                                    error={!!errors.creditCardHolderName}
                                    helperText={errors.creditCardHolderName}
                                />
                                <Stack direction="row" columnGap={2}>
                                    <TextField
                                        label="Mês de expiração (MM)"
                                        {...getFieldProps('creditCardExpiryMonth')}
                                        fullWidth
                                        error={!!errors.creditCardExpiryMonth}
                                        helperText={errors.creditCardExpiryMonth}
                                        inputProps={{ maxLength: 2 }}
                                    />
                                    <TextField
                                        label="Ano de expiração (AAAA)"
                                        {...getFieldProps('creditCardExpiryYear')}
                                        fullWidth
                                        error={!!errors.creditCardExpiryYear}
                                        helperText={errors.creditCardExpiryYear}
                                        inputProps={{ maxLength: 4 }}
                                    />
                                    <TextField
                                        label="CVV"
                                        {...getFieldProps('creditCardCcv')}
                                        sx={{ width: 150 }}
                                        error={!!errors.creditCardCcv}
                                        helperText={errors.creditCardCcv}
                                        inputProps={{ maxLength: 4 }}
                                    />
                                </Stack>

                                <FormControl fullWidth>
                                    <InputLabel>Parcelamento</InputLabel>
                                    <Select label="Parcelamento" {...getFieldProps('installmentCount')}>
                                        {[1, 2, 3, 4, 5].map(n => (
                                            <MenuItem key={n} value={String(n)}>
                                                {n}x parcela{n > 1 ? 's' : ''}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <Divider />
                                <Button
                                    variant="contained"
                                    sx={{ alignSelf: 'flex-end' }}
                                    onClick={handleSubmit}
                                    disabled={!isValid || isSubmitting}
                                    loading={isSubmitting}
                                >
                                    Processar pagamento
                                </Button>
                            </Stack>
                        )}

                        {(values.billingType === 'BOLETO' || values.billingType === 'PIX') && (
                            <>
                                <Divider />
                                <Button
                                    variant="contained"
                                    sx={{ alignSelf: 'flex-end' }}
                                    onClick={handleSubmit}
                                    disabled={isSubmitting}
                                    loading={isSubmitting}
                                >
                                    Gerar pagamento
                                </Button>
                            </>
                        )}
                    </>
                )}

                {/* Boleto/PIX gerado — aguardando compensação */}
                {customAmount > 0 && awaitingSlip && (
                    <>
                        <Alert severity="info">
                            Cobrança gerada. Assim que o pagamento for confirmado, a proposta segue automaticamente.
                        </Alert>
                        <Divider />
                        {(paymentData.billingType === 'BOLETO' ||
                            paymentData.invoiceUrl ||
                            paymentData.bankSlipUrl) && (
                            <Card>
                                <CardContent>
                                    <Stack
                                        direction="row"
                                        columnGap={2}
                                        alignItems="center"
                                        justifyContent="space-between"
                                    >
                                        <DocumentScannerRounded sx={{ fontSize: 56 }} color="action" />
                                        <Typography variant="body1" flex={1}>
                                            Seu boleto vence em até 5 dias úteis.
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            color="success"
                                            href={paymentData.invoiceUrl || paymentData.bankSlipUrl}
                                            target="_blank"
                                            rel="noopener"
                                        >
                                            Pagar boleto
                                        </Button>
                                    </Stack>
                                </CardContent>
                            </Card>
                        )}

                        {(paymentData.billingType === 'PIX' || paymentData.chargePix) && (
                            <Stack spacing={2} alignItems="center">
                                {paymentData.chargePix?.encodedImage && (
                                    <Box
                                        component="img"
                                        src={`data:image/png;base64,${paymentData.chargePix.encodedImage}`}
                                        alt="QR Code PIX"
                                        width={200}
                                        height={200}
                                        border={1}
                                        borderColor="text.secondary"
                                        borderRadius={1}
                                    />
                                )}
                                <TextField
                                    fullWidth
                                    value={paymentData.chargePix?.payload ?? ''}
                                    label="Código PIX"
                                    InputProps={{ readOnly: true }}
                                />
                                <Button
                                    variant="outlined"
                                    startIcon={<ContentCopyRounded />}
                                    onClick={handleCopyPix}
                                >
                                    Copiar código PIX
                                </Button>
                            </Stack>
                        )}
                    </>
                )}

                {customAmount > 0 && !showPaymentForm && !awaitingSlip && !isPaid && (
                    <Alert severity="info">
                        {payment_status[paymentStatus] ?? `Status: ${paymentStatus}`}
                    </Alert>
                )}
            </Stack>
        </Paper>
    );
}
